import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  GameMode,
  type AdminMatchListItem,
  type AdminMatchListResponse,
  type AdminMatchStatusFilter,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import StatusPill from '../../components/StatusPill';
import Pill from '../../components/Pill';
import Card from '../../components/Card';
import Alert from '../../components/Alert';
import PageHeader from '../../components/PageHeader';
import {
  listMatches,
  type ListMatchesParams,
} from '../../services/matches';
import { ApiError } from '../../services/api';
import { formatDateTime, formatDuration } from '../../utils/date';

const PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: 'all' | AdminMatchStatusFilter;
  label: string;
}> = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  // Narrower than Active (which also includes Matchmaking) — this is what the
  // dashboard's "Active games" card counts, and what it links to.
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'forfeit', label: 'Forfeit' },
];

function parseStatus(raw: string | null): 'all' | AdminMatchStatusFilter {
  if (
    raw === 'active' ||
    raw === 'in_progress' ||
    raw === 'completed' ||
    raw === 'forfeit'
  ) {
    return raw;
  }
  return 'all';
}

function parseMode(raw: string | null): GameMode | '' {
  if (raw === GameMode.Classic || raw === GameMode.Categories) return raw;
  return '';
}

function parseLanguage(raw: string | null): string {
  if (raw === 'en' || raw === 'tr') return raw;
  return '';
}

function parsePlayerId(raw: string | null): string {
  if (raw && /^[0-9a-fA-F]{24}$/.test(raw.trim())) return raw.trim();
  return '';
}

function parsePage(raw: string | null): number {
  if (!raw) return 1;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/** Convert a `datetime-local` value to ISO for the server. Empty → undefined. */
function datetimeLocalToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/**
 * Admin matches list. Status filter is persisted to the URL query string so
 * admins can bookmark / share a view of "only active matches". Pagination
 * uses the same query — `?status=active&page=2` is the source of truth, local
 * state is derived from `useSearchParams()`.
 *
 * Refetch keys on `(status, page)` only. A stale-response guard protects
 * against the filter being changed while the previous request is still
 * in-flight (admin hammers the dropdown faster than the network).
 */
export default function MatchesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = parseStatus(searchParams.get('status'));
  const modeFilter = parseMode(searchParams.get('mode'));
  const languageFilter = parseLanguage(searchParams.get('language'));
  const playerIdFilter = parsePlayerId(searchParams.get('playerId'));
  const fromRaw = searchParams.get('from') ?? '';
  const toRaw = searchParams.get('to') ?? '';
  const page = parsePage(searchParams.get('page'));

  // Draft state for the playerId input — the URL (and therefore the fetch
  // keys) only update after the user pauses typing for 300ms. Without this,
  // a full 24-char ObjectId fires 24 API calls. Draft re-syncs from the URL
  // whenever the param changes via back/forward or Clear filters.
  const [playerIdDraft, setPlayerIdDraft] = useState(playerIdFilter);
  useEffect(() => {
    setPlayerIdDraft(playerIdFilter);
  }, [playerIdFilter]);

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminMatchListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const reqCounterRef = useRef(0);
  const fetchMatches = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const params: ListMatchesParams = {
        page,
        limit: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        mode: modeFilter === '' ? undefined : modeFilter,
        language: languageFilter || undefined,
        playerId: playerIdFilter || undefined,
        from: datetimeLocalToIso(fromRaw),
        to: datetimeLocalToIso(toRaw),
      };
      const data = await listMatches(params);
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load matches';
      setState({ status: 'error', message });
    }
  }, [
    page,
    statusFilter,
    modeFilter,
    languageFilter,
    playerIdFilter,
    fromRaw,
    toRaw,
  ]);

  useEffect(() => {
    void fetchMatches();
  }, [fetchMatches]);

  // Debounce the playerId draft — same 300ms pattern as the audit viewer.
  // Only commits to the URL when the trimmed draft actually differs from
  // what's already there, so this doesn't loop-fight the `playerIdFilter →
  // draft` re-sync effect above.
  useEffect(() => {
    const trimmed = playerIdDraft.trim();
    if (trimmed === playerIdFilter) return;
    const handle = window.setTimeout(() => {
      patchFilter({ playerId: trimmed });
    }, 300);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerIdDraft, playerIdFilter]);

  /**
   * Single entry-point for every URL-synced filter change. Always resets
   * `page` because the new filter set almost certainly invalidates the
   * current page cursor. Empty string values delete the key so the URL
   * stays clean.
   */
  function patchFilter(patch: Record<string, string>) {
    const params = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) params.set(k, v);
      else params.delete(k);
    }
    params.delete('page');
    setSearchParams(params, { replace: true });
  }

  function handleStatusChange(next: 'all' | AdminMatchStatusFilter) {
    patchFilter({ status: next === 'all' ? '' : next });
  }

  function clearFilters() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function handlePageChange(next: number) {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(next));
    }
    setSearchParams(params, { replace: true });
  }

  const rows = useMemo<AdminMatchListItem[]>(() => {
    return state.status === 'ready' ? state.data.data : [];
  }, [state]);

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const columns: DataTableColumn<AdminMatchListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: 'Match',
        render: (m) => (
          <span
            className="font-mono text-xs text-slate-700"
            title={m.id}
            data-testid={`match-id-${m.id}`}
          >
            {shortId(m.id)}
          </span>
        ),
      },
      {
        key: 'mode',
        header: 'Mode',
        render: (m) => <Pill variant="neutral">{m.mode}</Pill>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (m) => <StatusPill status={m.status} />,
      },
      {
        key: 'language',
        header: 'Lang',
        render: (m) => (
          <span className="font-mono text-xs text-slate-700 uppercase">
            {m.language}
          </span>
        ),
      },
      {
        key: 'player1Id',
        header: 'Player 1',
        render: (m) => (
          <Link
            to={`/users/${encodeURIComponent(m.player1Id)}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-slate-700 hover:text-slate-900 hover:underline"
            title={m.player1Id}
          >
            {shortId(m.player1Id)}
          </Link>
        ),
      },
      {
        key: 'player2Id',
        header: 'Player 2',
        render: (m) =>
          m.seats && m.seats.length > 2 ? (
            <span className="text-xs text-slate-500" data-testid={`matches-row-${m.id}-more-seats`}>
              +{m.seats.length - 1} players
            </span>
          ) : (
          <Link
            to={`/users/${encodeURIComponent(m.player2Id)}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-slate-700 hover:text-slate-900 hover:underline"
            title={m.player2Id}
          >
            {shortId(m.player2Id)}
          </Link>
          ),
      },
      {
        key: 'score',
        header: 'Score',
        render: (m) => (
          <span className="font-mono text-slate-700">
            {m.seats && m.seats.length > 2
              ? m.seats.map((s) => s.score).join(' · ')
              : `${m.player1Score} – ${m.player2Score}`}
          </span>
        ),
      },
      {
        key: 'winnerId',
        header: 'Winner',
        render: (m) => <WinnerCell match={m} />,
      },
      {
        key: 'startedAt',
        header: 'Started',
        render: (m) => (
          <span className="text-xs text-slate-500">
            {formatDateTime(m.startedAt)}
          </span>
        ),
      },
      {
        key: 'endedAt',
        header: 'Ended',
        render: (m) => (
          <span className="text-xs text-slate-500">
            {m.endedAt ? formatDateTime(m.endedAt) : '—'}
          </span>
        ),
      },
      {
        key: 'duration',
        header: 'Duration',
        render: (m) => (
          <span className="text-xs font-mono text-slate-700">
            {formatDuration(m.startedAt, m.endedAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" data-testid="matches-list-screen">
      <PageHeader
        title="Matches"
        actions={
          <button
            type="button"
            onClick={clearFilters}
            data-testid="matches-clear-filters"
            className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Clear filters
          </button>
        }
      />

      <Card
        padding="sm"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
      >
        <label className="flex flex-col text-xs text-slate-600">
          Status
          <select
            value={statusFilter}
            onChange={(e) =>
              handleStatusChange(
                e.target.value as 'all' | AdminMatchStatusFilter,
              )
            }
            data-testid="matches-filter-status"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          Mode
          <select
            value={modeFilter}
            onChange={(e) => patchFilter({ mode: e.target.value })}
            data-testid="matches-filter-mode"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All modes</option>
            <option value={GameMode.Classic}>Classic</option>
            <option value={GameMode.Categories}>Categories</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          Language
          <select
            value={languageFilter}
            onChange={(e) => patchFilter({ language: e.target.value })}
            data-testid="matches-filter-language"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All languages</option>
            <option value="en">EN</option>
            <option value="tr">TR</option>
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600 lg:col-span-1">
          Player ID
          <input
            type="text"
            value={playerIdDraft}
            onChange={(e) => setPlayerIdDraft(e.target.value)}
            placeholder="24-char ObjectId"
            data-testid="matches-filter-player-id"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          From
          <input
            type="datetime-local"
            value={fromRaw}
            onChange={(e) => patchFilter({ from: e.target.value })}
            data-testid="matches-filter-from"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          To
          <input
            type="datetime-local"
            value={toRaw}
            onChange={(e) => patchFilter({ to: e.target.value })}
            data-testid="matches-filter-to"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>
      </Card>

      {state.status === 'error' && (
        <Alert testId="matches-list-error">{state.message}</Alert>
      )}

      <DataTable<AdminMatchListItem>
        columns={columns}
        rows={rows}
        rowKey={(m) => m.id}
        loading={state.status === 'loading'}
        onRowClick={(m) => navigate(`/matches/${m.id}`)}
        empty={
          statusFilter === 'all'
            ? 'No matches yet.'
            : `No ${statusFilter} matches.`
        }
        testId="matches-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

function WinnerCell({ match }: { match: AdminMatchListItem }) {
  // N-seat table (#679): name the winning SEAT, or say the 1st was shared.
  if (match.seats && match.seats.length > 2) {
    const first = match.seats.filter((s) => s.placement === 1);
    if (first.length === 0) return <span className="text-xs text-slate-500">—</span>;
    if (first.length > 1) return <Pill variant="neutral">Shared</Pill>;
    return <Pill variant="info">Seat {first[0].seat + 1}</Pill>;
  }
  if (!match.winnerId) {
    return <span className="text-xs text-slate-500">—</span>;
  }
  if (match.winnerId === match.player1Id) {
    return <Pill variant="info">P1</Pill>;
  }
  if (match.winnerId === match.player2Id) {
    return <Pill variant="accent">P2</Pill>;
  }
  // Winner doesn't match either player — surface the raw id rather than lie.
  return (
    <span
      className="font-mono text-xs text-slate-700"
      title={match.winnerId}
    >
      {shortId(match.winnerId)}
    </span>
  );
}

function shortId(id: string): string {
  if (id.length <= 10) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}
